/* eslint-disable @typescript-eslint/unbound-method */
// `Validators.required` is a safe static validator; the unbound-method rule
// false-positives on the bare reference (same file-level disable the basic form
// uses for the same reason).
import { Component, inject, input, model } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  type AccessPath,
  ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  type AssistantForAnalytics,
  type GroupForAnalytics,
  type TeammateForAnalytics,
} from '@shared';
import { environment } from '../../../../environments/environment';
import {
  AssistantBuilderFileUploadComponent,
  AssistantBuilderFileValue,
} from './assistant-builder-file-upload.component';
import { getFormErrorMessage } from './assistant-builder.validators';
import { TrimOnBlurDirective } from './trim-on-blur.directive';

export type AssistantBuilderTraitInsightForm = {
  [K in keyof AssistantBuilderTraitInsightFormValues]: FormControl<
    AssistantBuilderTraitInsightFormValues[K]
  >;
};

export interface AssistantBuilderTraitInsightFormValues {
  files: AssistantBuilderFileValue[];
  prompt: string;
}

@Component({
  selector: 'squadbox-assistant-builder-trait-insight-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    AssistantBuilderFileUploadComponent,
    fromSeriousSystem.TextareaComponent,
    TrimOnBlurDirective,
  ],
  template: ` @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <!-- Form: Prompt (Instructions) — rendered above Knowledge per Figma (§9) -->
      <sd-textarea
        squadboxTrimOnBlur
        size="lg"
        formControlName="prompt"
        [label]="'ASSISTANTS.SAVE_MODAL.FORM.PROMPT.LABEL' | translate"
        [required]="true"
        [placeholder]="
          'ASSISTANTS.SAVE_MODAL.FORM.PROMPT.PLACEHOLDER' | translate
        "
        [limit]="ASSISTANT_PROMPT_MAXLENGTH"
        [showCounter]="false"
        [showError]="shouldShowError(form.controls.prompt)"
        [errorMessage]="getErrorMessage(form.controls.prompt)"
      ></sd-textarea>
      <!-- Form: Files (Knowledge) -->
      <squadbox-assistant-builder-file-upload
        [label]="'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.LABEL' | translate"
        [placeholder]="
          'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.PLACEHOLDER' | translate
        "
        filePickerButtonIcon="plus"
        formControlName="files"
        [fileSupportedExtensions]="documentUploadSupportedExtensions"
        [transactionId]="fileUploadTransactionId()"
        [assistantForAnalytics]="assistantForAnalytics()"
        [assistantAccessPath]="assistantAccessPath()"
        [assistantAccessGroup]="assistantAccessGroup()"
        [assistantTeammate]="assistantTeammate()"
      ></squadbox-assistant-builder-file-upload>
    </form>`,
})
export class AssistantBuilderTraitInsightFormComponent {
  //Input/Output properties
  public readonly fileUploadTransactionId = input.required<string>();
  public readonly assistantForAnalytics = input<
    AssistantForAnalytics | undefined
  >();
  public readonly assistantAccessPath = input<AccessPath | undefined>();
  public readonly assistantAccessGroup = input<GroupForAnalytics | undefined>();
  public readonly assistantTeammate = input<TeammateForAnalytics | undefined>();

  //Readonly properties
  public readonly ASSISTANT_PROMPT_MAXLENGTH =
    environment.assistantPromptMaxLength;

  // Form — a non-empty prompt is REQUIRED for insight (SB-2972): the API rejects
  // a blank prompt for the 'insight' trait, so the form must gate Save rather
  // than let the create/update 400 into a generic error toast. `squadboxTrimOnBlur`
  // trims the field, so a whitespace-only value collapses to '' and fails
  // `required`.
  public readonly form = model<FormGroup<AssistantBuilderTraitInsightForm>>(
    new FormGroup<AssistantBuilderTraitInsightForm>({
      files: new FormControl<AssistantBuilderFileValue[]>(
        {
          value: [],
          disabled: false,
        },
        { nonNullable: true },
      ),
      prompt: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(this.ASSISTANT_PROMPT_MAXLENGTH),
        ],
      }),
    }),
  );

  public readonly documentUploadSupportedExtensions: string[] = [
    ...ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  private readonly translate = inject(TranslateService);

  // Show the inline error only once the user has interacted (SB-2972): the
  // prompt is required for insight, and this surfaces the '*'/error the way the
  // basic form does for Name/Description/Intro rather than silently gating Save.
  public shouldShowError(control: FormControl): boolean {
    return !!(control.invalid && (control.dirty || control.touched));
  }

  public getErrorMessage(control: FormControl): string | undefined {
    return getFormErrorMessage(control, this.translate) || undefined;
  }
}
